/**
 * iter128 — Front-end formatters for Australian phone + rego.
 *
 * Used across all customer-/vehicle-bearing forms (booking popup, JC
 * modal, invoice edit, kiosk check-in, vehicle edit, customer edit).
 * The same canonical formats are enforced server-side in
 * /app/backend/phone_helpers.py and /app/backend/rego_helpers.py — so
 * regardless of which surface the data was entered on, storage is
 * consistent and downstream search/lookup stays clean.
 *
 * Canonical forms:
 *   Mobile  : "0415 837 157" (4-3-3 groups, leading 0)
 *   Landline: "03 9876 5432" (2-4-4 groups, leading 0)  -- backend formats this
 *             but the front-end leaves it alone per the SM's spec (Q1=C).
 *   Rego    : "1OQ 4FV" / "ABC 123" / "ABC 1234" -- floor(len/2) split.
 */

const DIGITS = /[^\d]/g;
const ALNUM_NOT = /[^A-Z0-9]/g;

/**
 * Format an Australian phone number to the canonical spaced shape:
 *   Mobile  : "0415 837 157" (4-3-3)
 *   Landline: "03 9748 1234" (2-4-4) — per SM spec (iter228)
 * Only acts once the digits form a complete 10-digit AU number. Anything
 * else (international, partial digits) is returned mostly as-typed so the
 * SM can keep working without the input fighting them.
 *
 * Idempotent: passing the canonical output back returns the same value.
 */
export function formatAUMobile(raw) {
  if (raw === null || raw === undefined) return '';
  let s = String(raw).trim();
  if (!s) return '';
  // Strip +61 / 0061 prefixes — both turn into 0NN…
  const digits = s.replace(DIGITS, '');
  let local = digits;
  if (digits.startsWith('0061')) local = '0' + digits.slice(4);
  else if (digits.startsWith('61') && digits.length >= 11) local = '0' + digits.slice(2);
  // 9-digit mobile typed without the leading 0
  if (local.length === 9 && local.startsWith('4')) local = '0' + local;
  // Mobile: 10 digits starting 04 → 4-3-3
  if (local.length === 10 && local.startsWith('04')) {
    return `${local.slice(0, 4)} ${local.slice(4, 7)} ${local.slice(7, 10)}`;
  }
  // Landline: 10 digits starting 02/03/07/08 → 2-4-4
  if (local.length === 10 && ['02', '03', '07', '08'].includes(local.slice(0, 2))) {
    return `${local.slice(0, 2)} ${local.slice(2, 6)} ${local.slice(6, 10)}`;
  }
  // Else: leave the raw input alone (lets SM type a partial number
  // without surprises while still typing).
  return s;
}

/**
 * onChange helper for phone inputs. Live-formats AS the SM types, but
 * only commits the canonical form when the digits add up to 10 starting
 * with 04 (else keeps the raw user input — letting them backspace /
 * paste / fix typos without the field rewriting itself mid-edit).
 */
export function onChangePhone(value, setter) {
  setter(formatAUMobile(value));
}

/**
 * Format an Australian rego to the canonical SPACED uppercase form.
 * Split rule: floor(len/2). Examples:
 *    "1oq4fv"   → "1OQ 4FV"
 *    "abc123"   → "ABC 123"
 *    "abc1234"  → "ABC 1234"
 *    "ab123"    → "AB 123"
 * Leaves anything < 3 chars or > 10 chars as-typed (uppercased) so the
 * SM can still draft a partial entry.
 *
 * Idempotent.
 */
export function formatRego(raw) {
  if (raw === null || raw === undefined) return '';
  const cleaned = String(raw).toUpperCase().replace(ALNUM_NOT, '');
  if (!cleaned) return '';
  if (cleaned.length < 3 || cleaned.length > 10) return cleaned;
  const mid = Math.floor(cleaned.length / 2);
  return `${cleaned.slice(0, mid)} ${cleaned.slice(mid)}`;
}

export function onChangeRego(value, setter) {
  setter(formatRego(value));
}

/**
 * Return TRUE when `a` and `b` represent the same AU phone after
 * stripping non-digit chars and country-code variants. Handy for
 * deduping search results / detecting "same number" across formats.
 */
export function phoneEquals(a, b) {
  const fa = formatAUMobile(a || '').replace(DIGITS, '');
  const fb = formatAUMobile(b || '').replace(DIGITS, '');
  if (!fa || !fb) return false;
  return fa === fb;
}
